import { DOCUMENT } from '@angular/common';
import { Component, Inject, Input, OnDestroy, OnInit, Renderer2, ViewEncapsulation } from '@angular/core';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { WebSocketNotificationType, WebSocketTaskNotification } from 'src/app/core/websocket/model';
import { WebSocketService } from 'src/app/core/websocket/websocket.service';
import { TaskStateInWsNotif } from 'src/app/shared/models/misc.model';

import { TaskListConfiguration } from '../../inbox/shared/models';
import { PushboxService } from '../services/pushbox.service';
import { ChannelStockConfig } from '../shared/models';

@Component({
  selector: 'app-pushbox-left-navigation',
  templateUrl: './pushbox-left-navigation.component.html',
  styleUrls: ['./pushbox-left-navigation.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class PushboxLeftNavigationComponent implements OnInit, OnDestroy {
  @Input() taskListConfiguration: TaskListConfiguration;

  channels: ChannelStockConfig[] = [];

  parentContainer: HTMLElement;
  parentContainerId = 'alc-sub-nav';
  ngPushboxClass = 'ng-pushbox';

  private currentTitle: string;
  private titleBlinkInterval: any;

  private subscriptions = new Subscription();

  constructor(
    private alcmeonContextService: AlcmeonContextService,
    private pushboxService: PushboxService,
    private renderer: Renderer2,
    private webSocketService: WebSocketService,
    @Inject(DOCUMENT) public document: Document,
    @Inject('$state') private $state: any,
    @Inject('TopToolbarFactory') private topToolbarService: any
  ) {
    this.parentContainer = this.document.getElementById(this.parentContainerId);
  }

  ngOnInit(): void {
    this.renderer.addClass(this.parentContainer, this.ngPushboxClass);
    this.currentTitle = this.document.querySelector('title').innerText;
    this.channels = this.pushboxService.getAllChannelsTaskListConfigs();

    this.makeTitleBlink();

    this.pushboxService.getStockCounters(this.channels).subscribe(resp => {
      this.channels = resp;
      this.updateTodayPendingCounter();
    });

    // Task notification
    this.subscriptions.add(
      this.webSocketService.message$
        .pipe(filter((event: any) => event.type === WebSocketNotificationType.Task))
        .subscribe({
          next: (taskEvent: WebSocketTaskNotification) => {
            this.updateCounters(taskEvent);
          },
        })
    );
  }

  updateCounters(taskEvent: WebSocketTaskNotification) {
    if (this.channels.length > 0) {
      // Remove task from initial state array

      this.channels.some((channel, index) => {
        const taskIndex = channel.tasks_ids.indexOf(taskEvent.task_id);
        if (taskIndex !== -1) {
          channel.tasks_ids.splice(taskIndex, 1);

          // if index === 0, it's "total", and we need to find the channel too
          if (index > 0) {
            return true;
          }
        }
      });

      // Add task in new state array
      const company = this.alcmeonContextService.getCompany();
      const taskState = taskEvent.task_state;

      if (
        taskState === TaskStateInWsNotif.Pending ||
        taskState === TaskStateInWsNotif.HelpAnswered ||
        (taskState === TaskStateInWsNotif.Assigned && taskEvent.user_id === this.alcmeonContextService.self.id) ||
        (taskState === TaskStateInWsNotif.OnHold && taskEvent.user_id === company.quick_reply_bot_id)
      ) {
        let added = false;
        this.channels.some(channel => {
          if (channel.taskTypes.includes(taskEvent.task_type)) {
            channel.tasks_ids.push(taskEvent.task_id);
            added = true;
            return true;
          }
        });

        // "total" doesn't contain taskTypes so no way to identify if new task belongs to it
        if (added) {
          this.channels[0].tasks_ids.push(taskEvent.task_id);
        }
      }

      this.updateTodayPendingCounter();
    }
  }

  updateTodayPendingCounter() {
    this.topToolbarService.setPendingMessagesCounter(this.channels[0].tasks_ids.length);
  }

  /**
   * Make the title blink if we have at least one message in total counter, and if the browser active tab is not Alcméon.
   */
  private makeTitleBlink() {
    const setTitleFn = (title: string) => {
      this.document.querySelector('title').innerText = title;
    };

    clearInterval(this.titleBlinkInterval);
    let isTitleBlinking = false;

    this.titleBlinkInterval = setInterval(() => {
      if (
        !isTitleBlinking &&
        this.document.visibilityState === 'hidden' &&
        !this.$state.includes('**.process') &&
        this.channels[0].tasks_ids.length > 0
      ) {
        setTitleFn(`📩🔴 ${this.currentTitle}`);
        isTitleBlinking = true;
      } else {
        setTitleFn(this.currentTitle);
        isTitleBlinking = false;
      }
    }, 1000);
  }

  ngOnDestroy() {
    this.renderer.removeClass(this.parentContainer, this.ngPushboxClass);
    this.subscriptions.unsubscribe();
    clearInterval(this.titleBlinkInterval);
  }
}
